import { ValidationControlBase } from "@frui.ts/bootstrap";
import { bound } from "@frui.ts/helpers";
import { IBindingProps } from "@frui.ts/views";
import React from "react";
import AdvancedInput, { Transformation } from "controls/advancedInput";
import { euStars, uaFlag, swissLicensePlateSign } from "./assets/licensePlateAssets";
import { LabeledCountry, labeledCountries } from "./config";
import classNames from "classnames";

export interface LicensePlateInputProps {
  id: string;
  translate: (code: string) => string;
  locale: string;
  countryId?: string;
  countryCode?: string;
  disabled?: boolean;
  errorMessage?: string;
  disableCopy?: boolean;
  disablePaste?: boolean;
  onBlur?: (e: React.FormEvent<any>) => void;
  maxLength?: number;
}

export default class LicensePlateInput<TTarget> extends ValidationControlBase<
  TTarget,
  LicensePlateInputProps & IBindingProps<TTarget>
> {
  ignoredCharacters = new RegExp(/[^A-Z0-9]+/gi);

  @bound
  protected renderInner() {
    const {
      id,
      target,
      property,
      translate,
      locale,
      disabled = false,
      errorMessage,
      disablePaste,
      disableCopy,
      onBlur,
      countryCode,
      maxLength,
    } = this.props;

    const label = labeledCountries.find(x => x.countryCode === countryCode);
    const placeholder = locale == "cs" ? label?.placeholderCzech ?? label?.placeholder : label?.placeholder;
    const licensePlatePlaceholder = placeholder ?? translate("licensePlatePlaceholder");

    const labelClass = classNames({
      "country-label": true,
      europe: ["EU", "UA"].includes(label?.labelType ?? ""),
    });

    const inputPaddingClass = classNames({
      "labeled-country-input": label,
    });

    return (
      <div className="license-plate-input">
        {label && (
          <div className={labelClass}>
            {renderLabelIcon(label)}
            <span>{label.labelLetters}</span>
          </div>
        )}
        <AdvancedInput
          id={id}
          trim={true}
          ignoredCharacters={this.ignoredCharacters}
          transformation={Transformation.UpperCased}
          disabled={disabled}
          target={target}
          property={property}
          autoComplete="off"
          errorMessage={errorMessage}
          placeholder={licensePlatePlaceholder}
          disableCopy={disableCopy}
          disablePaste={disablePaste}
          onBlur={onBlur}
          className={inputPaddingClass}
          maxLength={maxLength}
        />
      </div>
    );
  }
}

export const renderLabelIcon = (label: LabeledCountry) => {
  switch (label.labelType) {
    case "EU":
      return <img className="eu-icon" src={euStars} alt="EU" />;
    case "UA":
      return <img className="ua-icon" src={uaFlag} alt="UA" />;
    case "CH":
      return <img className="ch-icon" src={swissLicensePlateSign} alt="CH" />;
  }
};
